import React, { useState, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { useParams } from '@thd-olt-component-react/router';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import {
  Row, Accordion, AccordionBody, AccordionTitle, Placeholder
} from '@thd-olt-component-react/core-ui';
import { QueryContext, useDataModel, extend } from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { DynamicComponent, AppContext } from '@thd-nucleus/app-render';
import { Hydrator, declareContexts } from '@thd-olt-component-react/hydrator';
import { ProductOverviewAccordionTitle } from '@thd-olt-component-react/product-overview';
import { QuestionsAndAnswersAccordionTitle } from '@thd-olt-component-react/questions-and-answers';
import {
  RatingsAndReviewsAccordionTitle,
  dataModel as RatingsAndReviewsComponentDataModel
} from '@thd-olt-component-react/ratings-and-reviews';
import {
  ProductOverviewAccordionBody, ProductOverviewAccordionBodyLoad, ProductOverviewAccordionBodyModel,
  QuestionsAndAnswersAccordionBody, QuestionsAndAnswersAccordionBodyLoad, QuestionsAndAnswersAccordionBodyModel,
  RatingsAndReviewsAccordionBody, RatingsAndReviewsAccordionBodyLoad, RatingsAndReviewsAccordionBodyModel,
} from './dynamic-imports';
import ZoneCard from './zone-card';
import { ClientOnlyProductDataModel, ProductDataModel } from './data-models';

import '../styles/product/collapsible-sections.scss';

const CollapsibleSections = ({ store }) => {
  declareContexts([QueryContext, ExperienceContext]);
  const productOverviewRef = useRef(null);
  const questionsRef = useRef(null);
  const ratingsRef = useRef(null);

  const qctx = useContext(QueryContext);
  const { instance } = useContext(AppContext);
  const { itemId } = useParams();
  const { storeId } = store;

  const { data: productData, loading } = useDataModel('product', {
    variables: {
      itemId,
      storeId
    },
    skip: !itemId,
    ssr: false
  });

  const thdSeoBotDetected = instance?.thdSEOBotDetection;
  const isHDPPSku = productData?.product?.identifiers?.skuClassification === 'hdpp';

  const [expanded, setExpanded] = useState('');

  const handleAccordionChange = (accordion, canCloseAccordion = true) => {
    if (!expanded[accordion] || canCloseAccordion) {
      setExpanded((prevState) => ({ ...prevState, [accordion]: !(prevState[accordion]) }));
    }
  };

  return loading
    ? (
      <Placeholder width="100%" height="200px" color="#F5F5F5" />
    )
    : (
      <div className="grid">
        <ErrorBoundary name="collapsible-sections">
          <DynamicComponent pageType="pip">
            <Hydrator
              id="collapsible-sections-accordion"
              className="grid collapsible-sections"
              placeholder={(
                <>
                  <div
                    id="product-section-overview"
                    className="accordion-placeholder"
                    ref={productOverviewRef}
                  >
                    <Placeholder width="100%" height="82px" color="#F5F5F5" />
                  </div>
                  <div
                    id="product-section-qa"
                    className="accordion-placeholder"
                  >
                    <Placeholder width="100%" height="82px" color="#F5F5F5" />
                  </div>
                  <div
                    id="product-section-rr"
                    className="accordion-placeholder"
                  >
                    <Placeholder width="100%" height="82px" color="#F5F5F5" />
                  </div>
                </>
              )}
            >
              <ZoneCard className="zone-card">
                <div
                  id="product-section-overview"
                  className="product-section-overview"
                  ref={productOverviewRef}
                >
                  <Accordion
                    icon={{ size: 'sm' }}
                    iconPosition="right"
                    key="product-details-accordion"
                    className="collapsible-section"
                    expanded={expanded['#product-section-overview'] || thdSeoBotDetected}
                    onChange={() => handleAccordionChange('#product-section-overview')}
                  >
                    <AccordionTitle className="accordion-title-container">
                      <ProductOverviewAccordionTitle
                        itemId={itemId}
                        layoutGroup={{
                          tag: Row,
                        }}
                        hydrator={{
                          className: 'grid'
                        }}
                        dynamic={{
                          pageType: 'pip'
                        }}
                      />
                    </AccordionTitle>
                    <AccordionBody>
                      <DynamicComponent pageType="pip">
                        <Hydrator
                          id="product-section-product-overview"
                          className="grid"
                          waitFor={ProductOverviewAccordionBodyLoad}
                          placeholder={(
                            <Placeholder
                              width="100%"
                              height="2000px"
                              color="#F5F5F5"
                            />
                          )}
                        >
                          <ProductOverviewAccordionBody
                            itemId={itemId}
                            showReturnMessage
                            revJet
                            layoutGroup={{
                              tag: Row,
                            }}
                            qctx={qctx}
                          />
                        </Hydrator>
                      </DynamicComponent>
                    </AccordionBody>
                  </Accordion>
                </div>
                <div
                  id="product-section-qa"
                  className="product-section-qa"
                  ref={questionsRef}
                >
                  <Accordion
                    key="questions-answers-accordion"
                    icon={{ size: 'sm' }}
                    iconPosition="right"
                    className="collapsible-section"
                    expanded={expanded['#product-section-qa'] || thdSeoBotDetected}
                    onChange={() => handleAccordionChange('#product-section-qa')}
                  >
                    <AccordionTitle className="accordion-title-container">
                      <QuestionsAndAnswersAccordionTitle
                        itemId={itemId}
                        layoutGroup={{
                          tag: Row,
                        }}
                        hydrator={{
                          className: 'grid'
                        }}
                        dynamic={{
                          pageType: 'pip'
                        }}
                      />
                    </AccordionTitle>
                    <AccordionBody>
                      <DynamicComponent pageType="pip">
                        <Hydrator
                          id="product-section-questions-answers"
                          className="grid"
                          waitFor={QuestionsAndAnswersAccordionBodyLoad}
                          placeholder={(
                            <Placeholder
                              width="100%"
                              height="700px"
                              color="#F5F5F5"
                            />
                          )}
                        >
                          <QuestionsAndAnswersAccordionBody
                            itemId={itemId}
                            seoPageNumber={1}
                            layoutGroup={{
                              tag: Row,
                            }}
                            qctx={qctx}
                          />
                        </Hydrator>
                      </DynamicComponent>
                    </AccordionBody>
                  </Accordion>
                </div>
                {!isHDPPSku
                  && (
                    <div
                      id="product-section-rr"
                      className="product-section-rr"
                      ref={ratingsRef}
                    >
                      <Accordion
                        key="ratings-reviews-accordion"
                        icon={{ size: 'sm' }}
                        iconPosition="right"
                        className="collapsible-section collapsible-section--bottom-border"
                        expanded={expanded['#product-section-rr'] || thdSeoBotDetected}
                        onChange={() => handleAccordionChange('#product-section-rr')}
                      >
                        <AccordionTitle className="accordion-title-container">
                          <RatingsAndReviewsAccordionTitle
                            itemId={itemId}
                            layoutGroup={{
                              tag: Row,
                            }}
                            hydrator={{
                              className: 'grid'
                            }}
                            dynamic={{
                              pageType: 'pip'
                            }}
                          />
                        </AccordionTitle>
                        <AccordionBody>
                          <DynamicComponent pageType="pip">
                            <Hydrator
                              id="product-section-ratings-reviews"
                              className="grid"
                              waitFor={RatingsAndReviewsAccordionBodyLoad}
                              placeholder={(
                                <Placeholder
                                  width="100%"
                                  height="2000px"
                                  color="#F5F5F5"
                                />
                              )}
                            >
                              <RatingsAndReviewsAccordionBody
                                itemId={itemId}
                                qctx={qctx}
                                prosAndCons
                              />
                            </Hydrator>
                          </DynamicComponent>
                        </AccordionBody>
                      </Accordion>
                    </div>
                  )}
              </ZoneCard>
            </Hydrator>
          </DynamicComponent>
        </ErrorBoundary>
      </div>
    );
};

CollapsibleSections.propTypes = {
  store: PropTypes.shape({
    storeId: PropTypes.string
  }).isRequired
};

CollapsibleSections.displayName = 'CollapsibleSections';

CollapsibleSections.dataModel = extend(
  ProductDataModel,
  ClientOnlyProductDataModel,
  RatingsAndReviewsComponentDataModel,
  ProductOverviewAccordionBodyModel,
  QuestionsAndAnswersAccordionBodyModel,
  RatingsAndReviewsAccordionBodyModel,
);

export default CollapsibleSections;
